import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownToLine, Layers, Satellite, Trash2, UploadCloud } from 'lucide-react';
import {
  GNSS_VERSION_PATTERN,
  KNOWN_GNSS_TYPES,
  deleteGnssFirmware,
  downloadGnssFirmwareBinary,
  listGnssFirmwares,
  uploadGnssFirmware,
} from '../api/GnssOtaApi';
import { ActionDrawer, Toast } from '../components/ui';
import styles from './GnssOtaManagement.module.scss';

const ALL_TYPES = '';

const emptyUploadForm = { gnssType: KNOWN_GNSS_TYPES[0], version: '', comment: '' };

const validateUploadForm = ({ gnssType, version }) => {
  const errors = {};
  if (!KNOWN_GNSS_TYPES.includes(gnssType)) {
    errors.gnssType = `Select one of: ${KNOWN_GNSS_TYPES.join(', ')}.`;
  }
  if (!GNSS_VERSION_PATTERN.test(version.trim())) {
    errors.version = "1-47 characters of letters, digits, '.', '_' or '-' (e.g. 4.15.1).";
  }
  return errors;
};

function GnssOtaManagement() {
  const [firmwares, setFirmwares] = useState([]);
  const [activeType, setActiveType] = useState(ALL_TYPES);
  const [drawer, setDrawer] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchFirmwares = useCallback(async () => {
    const response = await listGnssFirmwares();
    setFirmwares(response.data || []);
  }, []);

  useEffect(() => {
    fetchFirmwares().catch(error =>
      setErrorMessage(
        `Could not load GNSS firmware library: ${error.response?.data?.detail || error.message}`
      )
    );
  }, [fetchFirmwares]);

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const closeDrawer = () => {
    if (uploading) {
      return;
    }
    setDrawer(null);
    setUploadForm(emptyUploadForm);
    setUploadFile(null);
    setUploadProgress(0);
  };

  const openUploadDrawer = () => {
    // Pre-select the type when a known type filter is active
    setUploadForm({
      ...emptyUploadForm,
      gnssType: KNOWN_GNSS_TYPES.includes(activeType) ? activeType : emptyUploadForm.gnssType,
    });
    setDrawer('upload-firmware');
  };

  const uploadErrors = validateUploadForm(uploadForm);
  const uploadFormValid = Object.keys(uploadErrors).length === 0 && Boolean(uploadFile);

  const handleUploadFirmware = async () => {
    if (!uploadFormValid) {
      return;
    }
    const gnssType = uploadForm.gnssType.trim();
    const version = uploadForm.version.trim();
    try {
      setUploading(true);
      clearMessages();
      setUploadProgress(0);
      await uploadGnssFirmware(
        uploadFile,
        { gnssType, version, comment: uploadForm.comment.trim() },
        progressEvent =>
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
      );
      setSuccessMessage(`${gnssType} firmware ${version} added to the library.`);
      setDrawer(null);
      setUploadForm(emptyUploadForm);
      setUploadFile(null);
      setUploadProgress(0);
      await fetchFirmwares();
    } catch (error) {
      setErrorMessage(
        `Could not upload firmware: ${error.response?.data?.detail || error.message}`
      );
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async firmware => {
    try {
      setLoading(true);
      clearMessages();
      const response = await downloadGnssFirmwareBinary(firmware.gnss_type, firmware.version);
      saveBlob(
        new Blob([response.data]),
        firmware.filename || `${firmware.gnss_type}_${firmware.version}.bin`
      );
      setSuccessMessage(`Download for ${firmware.gnss_type} ${firmware.version} started.`);
    } catch (error) {
      setErrorMessage(
        `Could not download firmware: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      await deleteGnssFirmware(pendingDelete.id);
      setSuccessMessage(`${pendingDelete.label} removed.`);
      setPendingDelete(null);
      await fetchFirmwares();
    } catch (error) {
      setErrorMessage(
        `Could not remove firmware: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = bytes => {
    if (!bytes) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
  };
  const formatDate = isoString => (isoString ? new Date(isoString).toLocaleString() : '—');

  const typeCounts = useMemo(() => {
    const counts = {};
    firmwares.forEach(firmware => {
      counts[firmware.gnss_type] = (counts[firmware.gnss_type] || 0) + 1;
    });
    return counts;
  }, [firmwares]);

  const typeFilters = useMemo(
    () => [
      { id: ALL_TYPES, label: 'All types', count: firmwares.length },
      ...Object.keys(typeCounts)
        .sort((a, b) => a.localeCompare(b))
        .map(type => ({ id: type, label: type, count: typeCounts[type] })),
    ],
    [firmwares.length, typeCounts]
  );

  // Fall back to all types when the filtered type disappears (last file deleted)
  useEffect(() => {
    if (activeType && !typeCounts[activeType]) {
      setActiveType(ALL_TYPES);
    }
  }, [activeType, typeCounts]);

  const visibleFirmwares = activeType
    ? firmwares.filter(firmware => firmware.gnss_type === activeType)
    : firmwares;

  const showFieldError = field => uploadForm[field] !== '' && uploadErrors[field];

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>GNSS deployment</span>
          <h1>GNSS receiver firmware</h1>
          <p>
            Store GNSS receiver firmware files (e.g. Septentrio .suf). Upgrades are started per hub
            from its status page; the hub streams the file from the cloud into the receiver and
            reboots when finished.
          </p>
        </div>
      </header>

      {successMessage && (
        <Toast tone="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}

      {firmwares.length > 0 && (
        <nav className={styles.typeSwitcher} aria-label="GNSS type filter">
          {typeFilters.map(filter => (
            <button
              key={filter.id || 'all'}
              type="button"
              className={activeType === filter.id ? styles.typeActive : ''}
              onClick={() => setActiveType(filter.id)}
            >
              {filter.id ? <Satellite size={16} /> : <Layers size={16} />}
              <span>{filter.label}</span>
              <b>{filter.count}</b>
            </button>
          ))}
        </nav>
      )}

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Firmware library</h2>
            <p>
              Each file is identified by GNSS type and version; the upgrade command sent to a hub is{' '}
              <code>gnss_fw_&lt;type&gt;_&lt;version&gt;</code>.
            </p>
          </div>
          <button type="button" className={styles.primaryButton} onClick={openUploadDrawer}>
            <UploadCloud size={16} /> Upload firmware
          </button>
        </div>
        {visibleFirmwares.length ? (
          <div className={styles.tableWrapper}>
            <table className={styles.firmwareTable}>
              <thead>
                <tr>
                  <th>GNSS type</th>
                  <th>Version</th>
                  <th>Size</th>
                  <th>SHA-256</th>
                  <th>Comment</th>
                  <th>Uploaded</th>
                  <th className={styles.actionsHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleFirmwares.map(firmware => {
                  const deleting = pendingDelete?.id === firmware.id;
                  return (
                    <tr key={firmware.id}>
                      <td>
                        <span className={styles.typeBadge}>{firmware.gnss_type}</span>
                      </td>
                      <td className={styles.versionCell}>{firmware.version}</td>
                      <td>{formatFileSize(firmware.size)}</td>
                      <td>
                        <code className={styles.hash} title={firmware.sha256}>
                          {firmware.sha256 ? `${firmware.sha256.slice(0, 12)}…` : '—'}
                        </code>
                      </td>
                      <td className={styles.commentCell} title={firmware.comment || ''}>
                        {firmware.comment || '—'}
                      </td>
                      <td>{formatDate(firmware.created_at)}</td>
                      <td className={styles.actionsCell}>
                        {deleting ? (
                          <div className={styles.inlineConfirm}>
                            <span>Delete?</span>
                            <button type="button" onClick={confirmDelete} disabled={loading}>
                              Delete
                            </button>
                            <button type="button" onClick={() => setPendingDelete(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className={styles.rowActions}>
                            <button
                              type="button"
                              className={styles.iconButton}
                              onClick={() => handleDownload(firmware)}
                              disabled={loading}
                              title={`Download ${firmware.filename || 'firmware'}`}
                            >
                              <ArrowDownToLine size={15} />
                            </button>
                            <button
                              type="button"
                              className={styles.iconDangerButton}
                              onClick={() =>
                                setPendingDelete({
                                  id: firmware.id,
                                  label: `${firmware.gnss_type} firmware ${firmware.version}`,
                                })
                              }
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Satellite size={27} />
            <strong>No GNSS firmware files</strong>
            <p>Upload a receiver firmware file before upgrading a hub's GNSS receiver.</p>
          </div>
        )}
      </section>

      <ActionDrawer
        open={drawer === 'upload-firmware'}
        eyebrow="Firmware library"
        title="Upload GNSS firmware"
        description="Store a GNSS receiver firmware file for a GNSS type and version."
        onClose={closeDrawer}
        closeDisabled={uploading}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeDrawer}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleUploadFirmware}
              disabled={uploading || !uploadFormValid}
            >
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload firmware'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>GNSS type</span>
            <select
              value={uploadForm.gnssType}
              onChange={event =>
                setUploadForm(current => ({ ...current, gnssType: event.target.value }))
              }
              disabled={uploading}
              autoFocus
            >
              {KNOWN_GNSS_TYPES.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <small>The receiver vendor this firmware is for.</small>
          </label>
          <label>
            <span>Version</span>
            <input
              value={uploadForm.version}
              onChange={event =>
                setUploadForm(current => ({ ...current, version: event.target.value }))
              }
              placeholder="4.15.1"
              disabled={uploading}
            />
            {showFieldError('version') && (
              <small className={styles.fieldError}>{uploadErrors.version}</small>
            )}
          </label>
          <label>
            <span>
              Comment <small>(optional)</small>
            </span>
            <input
              value={uploadForm.comment}
              onChange={event =>
                setUploadForm(current => ({ ...current, comment: event.target.value }))
              }
              placeholder="e.g. mosaic-H release notes link"
              disabled={uploading}
            />
          </label>
          <label className={styles.fileDrop}>
            <UploadCloud size={28} />
            <span>{uploadFile ? uploadFile.name : 'Choose a firmware file'}</span>
            <small>
              {uploadFile
                ? formatFileSize(uploadFile.size)
                : 'Receiver firmware image, e.g. a Septentrio .suf file'}
            </small>
            <input
              type="file"
              onChange={event => setUploadFile(event.target.files?.[0] || null)}
              disabled={uploading}
            />
          </label>
          {uploading && (
            <progress className={styles.progress} max="100" value={uploadProgress}>
              {uploadProgress}%
            </progress>
          )}
        </div>
      </ActionDrawer>
    </div>
  );
}

export default GnssOtaManagement;
